import {
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { type Product, type ProductOption, type PortionSize, type SaleMode, type WeightEstimate, type ServingEstimate } from '../types';
import { productsCollection, productDoc } from '../lib/firestore';
import { initFirebase } from '../lib/firebase';
import { getTodayKey } from '../lib/date-utils';
import { ensureFreshToken } from '../lib/ensure-fresh-token';
import { reportError } from '../lib/error-reporting';

export async function listProducts(tenantId?: string): Promise<Product[]> {
  const { db } = initFirebase();
  // No need to wait for authentication - products are publicly readable

  // If tenantId is provided, filter by it; otherwise return all (for public access)
  const q = tenantId
    ? query(productsCollection(db), where('tenantId', '==', tenantId))
    : query(productsCollection(db));

  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export function onProductsSnapshot(
  callback: (products: Product[]) => void,
  tenantId?: string
): () => void {
  const { db } = initFirebase();

  // If tenantId is provided, filter by it; otherwise return all (for public access)
  const q = tenantId
    ? query(productsCollection(db), where('tenantId', '==', tenantId))
    : query(productsCollection(db));

  const unsub = onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => d.data()));
    },
    (error) => {
      reportError(error, { context: 'onProductsSnapshot', tenantId });
    }
  );
  return unsub;
}

export async function createProduct(input: {
  name: string;
  tenantId: string;
  saleMode: SaleMode;
  shortenName?: string;
  description?: string;
  picture?: string;
  section?: string;
  subSection?: string;
  notes?: string;
  price?: number;
  weightEstimate?: WeightEstimate;
  servingEstimate?: ServingEstimate;
  availableOptions?: ProductOption[];
  portionSizes?: PortionSize[];
  displayOrder?: number;
  isActive?: boolean;
  disclaimer?: string;
  marketIds?: string[];
}): Promise<Product> {
  if (!input.name || !input.name.trim()) {
    throw new Error('Le nom du produit est requis.');
  }
  if (!input.tenantId || !input.tenantId.trim()) {
    throw new Error('Le tenant ID est requis.');
  }

  // Validate availableOptions if provided
  if (input.availableOptions) {
    for (const opt of input.availableOptions) {
      if (!opt.id || !opt.label) {
        throw new Error('Chaque option doit avoir un id et un label.');
      }
      // Validate select/multiselect options have choices
      if ((opt.type === 'select' || opt.type === 'multiselect') && (!opt.choices || opt.choices.length === 0)) {
        throw new Error(`L'option "${opt.label}" de type ${opt.type} doit avoir au moins un choix.`);
      }
      // Validate choices have id and label
      if (opt.choices) {
        for (const choice of opt.choices) {
          if (!choice.id || !choice.label) {
            throw new Error(`Chaque choix de l'option "${opt.label}" doit avoir un id et un label.`);
          }
        }
      }
      // Validate composition options have components and totalRequired
      if (opt.type === 'composition') {
        if (!opt.components || opt.components.length === 0) {
          throw new Error(`L'option "${opt.label}" de type composition doit avoir au moins un composant.`);
        }
        if (!opt.totalRequired || opt.totalRequired <= 0) {
          throw new Error(`L'option "${opt.label}" de type composition doit avoir un total requis supérieur à 0.`);
        }
        for (const comp of opt.components) {
          if (!comp.productId || !comp.label) {
            throw new Error(`Chaque composant de l'option "${opt.label}" doit avoir un productId et un label.`);
          }
        }
      }
    }
  }

  const { db, currentUserPromise } = initFirebase();
  // Wait for authentication to complete
  await currentUserPromise;
  const id = nanoid(12);
  const product: Product = {
    id,
    name: input.name.trim(),
    shortenName: input.shortenName?.trim() || undefined,
    description: input.description?.trim() || undefined,
    picture: input.picture?.trim() || undefined,
    section: input.section?.trim() || undefined,
    subSection: input.subSection?.trim() || undefined,
    notes: input.notes?.trim() || undefined,
    displayOrder: input.displayOrder,
    tenantId: input.tenantId.trim(),
    isActive: input.isActive ?? true,

    // Pricing fields
    saleMode: input.saleMode,
    price: input.price && input.price > 0 ? input.price : undefined,

    // Mode-specific fields
    weightEstimate: input.weightEstimate || undefined,
    servingEstimate: input.servingEstimate || undefined,
    portionSizes: input.portionSizes || undefined,

    // Common fields
    disclaimer: input.disclaimer?.trim() || undefined,
    availableOptions: input.availableOptions || undefined,

    // Market availability
    marketIds: input.marketIds && input.marketIds.length > 0 ? input.marketIds : undefined,
  };

  // Utiliser setDoc avec l'ID spécifique au lieu de addDoc qui génère un ID aléatoire
  await setDoc(productDoc(db, id), product);
  return product;
}

export async function updateProduct(product: Product): Promise<void> {
  if (!product.id) throw new Error('ID produit manquant.');
  if (!product.name || !product.name.trim()) {
    throw new Error('Le nom du produit est requis.');
  }

  // Validate availableOptions if provided
  if (product.availableOptions) {
    for (const opt of product.availableOptions) {
      if (!opt.id || !opt.label) {
        throw new Error('Chaque option doit avoir un id et un label.');
      }
      // Validate select/multiselect options have choices
      if ((opt.type === 'select' || opt.type === 'multiselect') && (!opt.choices || opt.choices.length === 0)) {
        throw new Error(`L'option "${opt.label}" de type ${opt.type} doit avoir au moins un choix.`);
      }
      // Validate choices have id and label
      if (opt.choices) {
        for (const choice of opt.choices) {
          if (!choice.id || !choice.label) {
            throw new Error(`Chaque choix de l'option "${opt.label}" doit avoir un id et un label.`);
          }
        }
      }
      // Validate composition options have components and totalRequired
      if (opt.type === 'composition') {
        if (!opt.components || opt.components.length === 0) {
          throw new Error(`L'option "${opt.label}" de type composition doit avoir au moins un composant.`);
        }
        if (!opt.totalRequired || opt.totalRequired <= 0) {
          throw new Error(`L'option "${opt.label}" de type composition doit avoir un total requis supérieur à 0.`);
        }
        for (const comp of opt.components) {
          if (!comp.productId || !comp.label) {
            throw new Error(`Chaque composant de l'option "${opt.label}" doit avoir un productId et un label.`);
          }
        }
      }
    }
  }

  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  // Wait for authentication to complete
  await currentUserPromise;
  const ref = productDoc(db, product.id);

  const updateData = {
    name: product.name.trim(),
    shortenName: product.shortenName?.trim() || null,
    description: product.description?.trim() || null,
    picture: product.picture?.trim() || null,
    imageUrl: product.imageUrl || null,
    thumbnailUrl: product.thumbnailUrl || null,
    imageUpdatedAt: product.imageUpdatedAt || null,
    section: product.section?.trim() || null,
    subSection: product.subSection?.trim() || null,
    notes: product.notes?.trim() || null,
    displayOrder: product.displayOrder ?? null,
    isActive: product.isActive ?? null,

    // Pricing fields
    saleMode: product.saleMode,
    price: product.price !== undefined && product.price > 0 ? product.price : null,

    // Mode-specific fields
    weightEstimate: product.weightEstimate || null,
    servingEstimate: product.servingEstimate || null,
    portionSizes: product.portionSizes || null,

    // Common fields
    disclaimer: product.disclaimer?.trim() || null,
    availableOptions: product.availableOptions || null,

    // Market availability
    marketIds: product.marketIds && product.marketIds.length > 0 ? product.marketIds : null,
  };

  await updateDoc(ref, updateData);
}

export async function deleteProductById(id: string): Promise<void> {
  // Ensure we have a fresh token before deleting
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  // Wait for authentication to complete
  await currentUserPromise;
  const ref = productDoc(db, id);
  await deleteDoc(ref);
}

/**
 * Update display order for multiple products (for drag and drop reordering)
 */
export async function updateProductsOrder(updates: Array<{ id: string; displayOrder: number }>): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  // Update each product's displayOrder
  await Promise.all(
    updates.map(({ id, displayOrder }) =>
      updateDoc(productDoc(db, id), { displayOrder })
    )
  );
}

/**
 * Set a product's out-of-stock status for the current day
 * @param productId The product ID
 * @param isOutOfStock true to mark as out of stock, false to mark as available
 * Note: Caller should invalidate products cache after calling this function
 */
export async function setProductOutOfStock(productId: string, isOutOfStock: boolean): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  const ref = productDoc(db, productId);
  const outOfStockDate = isOutOfStock ? getTodayKey() : null;

  await updateDoc(ref, { outOfStockDate });
}

/**
 * Set a product's active status (for seasonal/disabled products)
 * @param productId The product ID
 * @param isActive true to activate, false to deactivate (hide from customers)
 * Note: Caller should invalidate products cache after calling this function
 */
export async function setProductActive(productId: string, isActive: boolean): Promise<void> {
  // Ensure we have a fresh token before updating
  await ensureFreshToken();

  const { db, currentUserPromise } = initFirebase();
  await currentUserPromise;

  const ref = productDoc(db, productId);
  await updateDoc(ref, { isActive });
}
